import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
  Input,
  message,
  theme,
} from "antd";
import {
  PlusOutlined,
  BugOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title } = Typography;

const STATUS_CONFIG = {
  open: { color: "error", icon: <ExclamationCircleOutlined />, label: "Open" },
  in_progress: { color: "processing", icon: <ClockCircleOutlined />, label: "In Progress" },
  resolved: { color: "success", icon: <CheckCircleOutlined />, label: "Resolved" },
  closed: { color: "default", icon: <CloseCircleOutlined />, label: "Closed" },
};

const PRIORITY_COLORS = {
  low: "blue",
  medium: "orange",
  high: "red",
  critical: "magenta",
};

const CATEGORY_OPTIONS = [
  { value: "ui", label: "UI / Frontend" },
  { value: "backend", label: "Backend / API" },
  { value: "data", label: "Data Issue" },
  { value: "engine", label: "Engine" },
  { value: "other", label: "Other" },
];

export default function BugReportList() {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({});
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ status: "", priority: "", category: "" });
  const [searchText, setSearchText] = useState("");

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const authToken = localStorage.getItem("token");
      const params = new URLSearchParams({
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      if (filters.status) params.append("status", filters.status);
      if (filters.priority) params.append("priority", filters.priority);
      if (filters.category) params.append("category", filters.category);

      const res = await fetch(`/api/bug-reports?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const result = await res.json();
      if (result.success) {
        setReports(result.data);
        setPagination((prev) => ({ ...prev, total: result.pagination.total }));
      }
    } catch (err) {
      message.error("Failed to load bug reports");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("token");
      const res = await fetch("/api/bug-reports/stats", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const result = await res.json();
      if (result.success) setStats(result.data);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [fetchReports, fetchStats]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1, pageSize }));
  }, [pageSize]);

  const filteredReports = reports.filter((r) => {
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    return (
      (r.title || "").toLowerCase().includes(s) ||
      (r.id || "").toLowerCase().includes(s) ||
      (r.reporter_name || "").toLowerCase().includes(s)
    );
  });

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 160,
      render: (id) => (
        <Button type="link" onClick={() => navigate(`/bug-reports/${id}`)} style={{ padding: 0, fontSize: 12 }}>
          {id.slice(0, 20)}...
        </Button>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title, record) => (
        <Button type="link" onClick={() => navigate(`/bug-reports/${record.id}`)} style={{ padding: 0 }}>
          {title}
        </Button>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => {
        const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
        return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
      },
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (p) => <Tag color={PRIORITY_COLORS[p]}>{(p || "medium").toUpperCase()}</Tag>,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (c) => c ? <Tag>{c}</Tag> : "-",
    },
    {
      title: "Reporter",
      dataIndex: "reporter_name",
      key: "reporter_name",
      width: 140,
    },
    {
      title: "Comments",
      dataIndex: "comment_count",
      key: "comment_count",
      width: 90,
      align: "center",
      render: (c) => c || 0,
    },
    {
      title: "Files",
      dataIndex: "attachment_count",
      key: "attachment_count",
      width: 70,
      align: "center",
      render: (c) => c || 0,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (d) => d ? dayjs(d).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 150,
      render: (d) => d ? dayjs(d).format("YYYY-MM-DD HH:mm") : "-",
    },
  ];

  return (
    <PageLayout active="bugReports">
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              <BugOutlined style={{ marginRight: 8 }} />
              Bug Reports
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/bug-reports/new")}
            >
              Report Bug
            </Button>
          </Col>
        </Row>

        {/* Stats */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic title="Open" value={stats.open_count || 0} valueStyle={{ color: token.colorError }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic title="In Progress" value={stats.in_progress_count || 0} valueStyle={{ color: token.colorPrimary }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic title="Resolved" value={stats.resolved_count || 0} valueStyle={{ color: token.colorSuccess }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic title="Total" value={stats.total || 0} />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                placeholder="Search by title, ID, or reporter..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 140 }}
                value={filters.status || undefined}
                onChange={(v) => setFilters((f) => ({ ...f, status: v || "" }))}
                options={[
                  { value: "open", label: "Open" },
                  { value: "in_progress", label: "In Progress" },
                  { value: "resolved", label: "Resolved" },
                  { value: "closed", label: "Closed" },
                ]}
              />
            </Col>
            <Col>
              <Select
                placeholder="Priority"
                allowClear
                style={{ width: 120 }}
                value={filters.priority || undefined}
                onChange={(v) => setFilters((f) => ({ ...f, priority: v || "" }))}
                options={[
                  { value: "low", label: "Low" },
                  { value: "medium", label: "Medium" },
                  { value: "high", label: "High" },
                  { value: "critical", label: "Critical" },
                ]}
              />
            </Col>
            <Col>
              <Select
                placeholder="Category"
                allowClear
                style={{ width: 140 }}
                value={filters.category || undefined}
                onChange={(v) => setFilters((f) => ({ ...f, category: v || "" }))}
                options={CATEGORY_OPTIONS}
              />
            </Col>
            <Col>
              <Button icon={<ReloadOutlined />} onClick={() => { fetchReports(); fetchStats(); }}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredReports}
            rowKey="id"
            loading={loading}
            pagination={makePagination(pageSize, setPageSize, {
              current: pagination.page,
              total: pagination.total,
              onChange: (page) => setPagination((p) => ({ ...p, page })),
            })}
            size="small"
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
